import { useMemo, useState } from "react";

import { Button } from "atoms/button/Button";
import { TextButton } from "atoms/text-button/TextButton";

import { useTranslation } from "@/core/i18n";

import { PersonalisationHeading } from "./PersonalisationHeading";
import { PersonalisationProgressBar } from "./PersonalisationProgressBar";
import { PersonalisationScreenLayout } from "./PersonalisationScreenLayout";
import { SingleChoiceTagPicker } from "./SingleChoiceTagPicker";

interface HeritageItem {
  id: string;
  label: string;
}

interface HeritageProps {
  options?: HeritageItem[];
  onContinue?: (selection: string) => void;
  onSkip?: () => void;
  selectedColor?: string;
  continueButtonText?: string;
  skipText?: string;
  progressPercentage: number;
  onBack?: () => void;
  onClose?: () => void;
  initialSelectedId?: string | null;
}

export const Heritage = ({
  options,
  onContinue,
  onSkip,
  selectedColor,
  continueButtonText,
  skipText,
  progressPercentage,
  onBack,
  onClose,
  initialSelectedId,
}: HeritageProps) => {
  const { t } = useTranslation();
  const defaultHeritageOptions = useMemo<HeritageItem[]>(
    () => [
      { id: "WhiteOrEuropean", label: t("heritage.optionWhiteOrEuropean") },
      { id: "HispanicOrLatino", label: t("heritage.optionHispanicOrLatino") },
      {
        id: "BlackAfricanOrAfricanAmerican",
        label: t("heritage.optionBlackOrAfricanAmerican"),
      },
      { id: "Asian", label: t("heritage.optionAsian") },
      {
        id: "MiddleEasternOrNorthAfrican",
        label: t("heritage.optionMiddleEasternOrNorthAfrican"),
      },
      {
        id: "NativeHawaiianOrPacificIslander",
        label: t("heritage.optionNativeHawaiianOrPacificIslander"),
      },
      {
        id: "NativeAmericanOrAlaskaNative",
        label: t("heritage.optionNativeAmericanOrAlaskaNative"),
      },
    ],
    [t],
  );
  const resolvedOptions = options ?? defaultHeritageOptions;
  const resolvedContinueButtonText = continueButtonText ?? t("common.continue");
  const resolvedSkipText = skipText ?? t("heritage.skipForNow");
  const [selected, setSelected] = useState<HeritageItem | null>(
    initialSelectedId ? (resolvedOptions.find((o) => o.id === initialSelectedId) ?? null) : null,
  );

  const handleSelectionChange = (item: HeritageItem | null) => {
    setSelected(item);
    onContinue?.(item?.id ?? "");
  };

  const handleContinue = () => {
    if (!selected) {
      return;
    }
    onContinue?.(selected.id);
  };

  const handleSkip = () => {
    onSkip?.();
  };

  const handleBack = () => {
    onBack?.();
  };

  const handleClose = () => {
    onClose?.();
  };

  const isContinueDisabled = selected === null;

  return (
    <PersonalisationScreenLayout
      header={
        <PersonalisationProgressBar
          progressPercentage={progressPercentage}
          onBack={handleBack}
          onClose={handleClose}
        />
      }
      footer={
        <>
          <Button
            style="primary"
            size="large"
            fullWidth
            disabled={isContinueDisabled}
            onClick={handleContinue}
          >
            {resolvedContinueButtonText}
          </Button>

          <TextButton variant="secondary" onClick={handleSkip}>
            {resolvedSkipText}
          </TextButton>
        </>
      }
    >
      <PersonalisationHeading size="sm">{t("heritage.heading")}</PersonalisationHeading>

      <SingleChoiceTagPicker
        items={resolvedOptions}
        selectedId={selected?.id ?? null}
        onSelectionChangeRequest={handleSelectionChange}
        selectedColor={selectedColor}
        ariaLabel={t("heritage.ariaLabel")}
      />
    </PersonalisationScreenLayout>
  );
};
